import { useEffect, useRef, useState } from "react";
import { useRobot } from "@/context/RobotContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  VideoCamera,
  VideoCameraSlash,
  Record,
  Broadcast,
  DeviceMobileCamera,
  LinkSimple,
  ArrowClockwise,
  Camera,
  Circle,
  StopCircle,
} from "@phosphor-icons/react";

const PLACEHOLDER = "https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg";

export default function CameraFeed() {
  const { pushLog } = useRobot();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const imgRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const [mode, setMode] = useState("device"); // device | url
  const [live, setLive] = useState(false); // device stream active
  const [recording, setRecording] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [streamUrl, setStreamUrl] = useState(""); // currently displayed robot stream
  const [urlError, setUrlError] = useState(false);
  const [clock, setClock] = useState("");

  useEffect(() => {
    const t = setInterval(() => setClock(new Date().toLocaleTimeString()), 1000);
    return () => clearInterval(t);
  }, []);

  // Load saved camera config
  useEffect(() => {
    api
      .get("/settings")
      .then((res) => {
        const cam = res.data.camera || {};
        if (cam.mode) setMode(cam.mode);
        if (cam.url) {
          setUrlInput(cam.url);
          if (cam.mode === "url") setStreamUrl(cam.url);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => () => stopStream(), []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const persistCamera = (patch) => {
    api.put("/settings", { camera: { mode, url: urlInput, ...patch } }).catch(() => {});
  };

  const toggleDeviceFeed = async () => {
    if (live) {
      stopStream();
      setLive(false);
      pushLog("Device camera feed stopped", "vision", "warning");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setLive(true);
      pushLog("Device camera online — streaming from this device", "vision", "success");
    } catch (e) {
      toast.error("Camera access denied");
      pushLog(`Camera error: ${e.message}`, "vision", "error");
    }
  };

  const loadRobotStream = () => {
    const u = urlInput.trim();
    if (!u) {
      toast.error("Enter the robot camera URL first");
      return;
    }
    if (window.location.protocol === "https:" && u.startsWith("http://")) {
      toast.warning("This is an http:// stream on an https page — your browser will block it. Open MechaLog over http/localhost on the same network as the robot.");
      pushLog("Mixed content: http stream blocked on https page", "vision", "warning");
    }
    setUrlError(false);
    setStreamUrl(`${u}${u.includes("?") ? "&" : "?"}_t=${Date.now()}`);
    persistCamera({ mode: "url", url: u });
    pushLog(`Loading robot stream: ${u}`, "vision", "info");
  };

  const stopRobotStream = () => {
    setStreamUrl("");
    pushLog("Robot stream stopped", "vision", "warning");
  };

  const switchMode = (m) => {
    if (m === mode) return;
    if (m === "url" && live) {
      stopStream();
      setLive(false);
    }
    setMode(m);
    persistCamera({ mode: m });
  };

  const takeSnapshot = () => {
    const srcEl = mode === "device" ? videoRef.current : imgRef.current;
    if (!showingMedia || !srcEl) {
      toast.error("No live feed to capture");
      return;
    }
    const w = (mode === "device" ? srcEl.videoWidth : srcEl.naturalWidth) || 0;
    const h = (mode === "device" ? srcEl.videoHeight : srcEl.naturalHeight) || 0;
    if (!w || !h) {
      toast.error("Stream not available to capture yet");
      return;
    }
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    try {
      canvas.getContext("2d").drawImage(srcEl, 0, 0, w, h);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = `mechalog-snapshot-${Date.now()}.png`;
      a.click();
      pushLog("Snapshot captured", "vision", "success");
      toast.success("Snapshot saved");
    } catch (e) {
      toast.error("Snapshot blocked by the camera's CORS policy");
      pushLog("Snapshot blocked (cross-origin stream)", "vision", "error");
    }
  };

  const toggleRecord = () => {
    if (recording) {
      recorderRef.current?.stop();
      return;
    }
    if (mode !== "device" || !streamRef.current) {
      toast.error("Recording requires the Device camera to be live");
      return;
    }
    try {
      chunksRef.current = [];
      const mr = new MediaRecorder(streamRef.current, { mimeType: "video/webm" });
      mr.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `mechalog-recording-${Date.now()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
        setRecording(false);
        pushLog("Recording saved", "vision", "success");
      };
      mr.start();
      recorderRef.current = mr;
      setRecording(true);
      pushLog("Recording started", "vision", "info");
    } catch (e) {
      toast.error("Recording not supported in this browser");
    }
  };

  const showingMedia = mode === "device" ? live : !!streamUrl;

  return (
    <div className="border border-[#272A35] bg-[#111318] h-full flex flex-col" data-testid="camera-feed-container">
      <div className="border-b border-[#272A35] px-3 sm:px-4 py-3 flex flex-wrap items-center justify-between gap-2">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#94A3B8] flex items-center gap-2">
          <Broadcast size={16} weight="bold" className="text-[#FF5A00]" /> Robot Vision
        </span>
        <div className="flex items-center gap-2">
          {/* snapshot + record */}
          {showingMedia && (
            <>
              <button
                data-testid="camera-snapshot-btn"
                onClick={takeSnapshot}
                title="Save a snapshot"
                className="p-1.5 border border-[#272A35] text-[#94A3B8] hover:border-[#FF5A00] hover:text-[#FF5A00] transition-colors active:scale-95"
              >
                <Camera size={15} weight="bold" />
              </button>
              <button
                data-testid="camera-record-btn"
                onClick={toggleRecord}
                title={recording ? "Stop recording" : "Record (device camera)"}
                className={`p-1.5 border transition-colors active:scale-95 ${
                  recording
                    ? "border-[#FF3B30] text-[#FF3B30] rec-dot"
                    : "border-[#272A35] text-[#94A3B8] hover:border-[#FF3B30] hover:text-[#FF3B30]"
                }`}
              >
                {recording ? <StopCircle size={15} weight="fill" /> : <Circle size={15} weight="fill" />}
              </button>
            </>
          )}
          {/* source picker */}
          <div className="flex border border-[#272A35]">
            <button
              data-testid="camera-mode-device"
              onClick={() => switchMode("device")}
              className={`flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                mode === "device" ? "bg-[#FF5A00] text-[#0A0B0E] font-bold" : "text-[#94A3B8] hover:bg-[#1A1D24]"
              }`}
            >
              <DeviceMobileCamera size={13} weight="bold" /> Device
            </button>
            <button
              data-testid="camera-mode-url"
              onClick={() => switchMode("url")}
              className={`flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-widest transition-colors ${
                mode === "url" ? "bg-[#FF5A00] text-[#0A0B0E] font-bold" : "text-[#94A3B8] hover:bg-[#1A1D24]"
              }`}
            >
              <LinkSimple size={13} weight="bold" /> Robot URL
            </button>
          </div>

          {mode === "device" && (
            <button
              data-testid="camera-toggle-btn"
              onClick={toggleDeviceFeed}
              className={`text-xs uppercase tracking-widest font-bold px-3 py-1.5 border transition-colors active:scale-95 flex items-center gap-1.5 ${
                live
                  ? "border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#0A0B0E]"
                  : "border-[#00E676] text-[#00E676] hover:bg-[#00E676] hover:text-[#0A0B0E]"
              }`}
            >
              {live ? <><VideoCameraSlash size={14} weight="bold" /> Stop</> : <><VideoCamera size={14} weight="bold" /> Go Live</>}
            </button>
          )}
          {mode === "url" && streamUrl && (
            <button
              data-testid="camera-stop-url-btn"
              onClick={stopRobotStream}
              className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 border border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-[#0A0B0E] transition-colors active:scale-95 flex items-center gap-1.5"
            >
              <VideoCameraSlash size={14} weight="bold" /> Stop
            </button>
          )}
        </div>
      </div>

      {/* URL input row */}
      {mode === "url" && (
        <div className="border-b border-[#272A35] px-3 sm:px-4 py-2 flex items-stretch gap-2">
          <input
            data-testid="camera-url-input"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadRobotStream()}
            placeholder="http://192.168.4.1:81/stream  (ESP32-CAM / Pi MJPEG URL)"
            className="flex-1 min-w-0 bg-[#0A0B0E] border border-[#272A35] px-3 py-2 text-xs font-mono focus:outline-none focus:border-[#FF5A00]"
          />
          <button
            data-testid="camera-load-url-btn"
            onClick={loadRobotStream}
            className="px-3 bg-[#FF5A00] text-[#0A0B0E] text-xs font-bold uppercase tracking-widest active:scale-95 flex items-center gap-1"
          >
            <VideoCamera size={14} weight="bold" /> Load
          </button>
        </div>
      )}

      <div className="relative flex-1 bg-black overflow-hidden group min-h-[280px]">
        {!showingMedia && (
          <img src={PLACEHOLDER} alt="feed offline" className="absolute inset-0 w-full h-full object-cover opacity-40 grayscale" />
        )}

        {mode === "device" && (
          <video ref={videoRef} autoPlay playsInline muted className={`absolute inset-0 w-full h-full object-cover ${live ? "opacity-100" : "opacity-0"}`} />
        )}
        {mode === "url" && streamUrl && (
          <img
            key={streamUrl}
            ref={imgRef}
            crossOrigin="anonymous"
            data-testid="camera-stream-img"
            src={streamUrl}
            alt="robot stream"
            onError={() => setUrlError(true)}
            onLoad={() => setUrlError(false)}
            className="absolute inset-0 w-full h-full object-contain bg-black"
          />
        )}

        {/* crosshair overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 border border-[#FF5A00]/50" />
          <div className="absolute left-1/2 top-0 bottom-0 w-px bg-[#FF5A00]/10" />
          <div className="absolute top-1/2 left-0 right-0 h-px bg-[#FF5A00]/10" />
          {showingMedia && !urlError && <div className="scan-line" />}
        </div>

        <div className="absolute top-3 left-3 flex items-center gap-2 bg-[#0A0B0E]/70 px-2 py-1">
          <Record size={12} weight="fill" className={`text-[#FF3B30] ${showingMedia && !urlError ? "rec-dot" : "opacity-40"}`} />
          <span className="text-[10px] font-bold tracking-widest uppercase">{showingMedia && !urlError ? "LIVE" : "OFFLINE"}</span>
        </div>

        <div className="absolute bottom-3 right-3 text-[10px] font-mono text-[#00E676] bg-[#0A0B0E]/70 px-2 py-1">{clock}</div>
        <div className="absolute bottom-3 left-3 text-[10px] font-mono text-[#94A3B8] bg-[#0A0B0E]/70 px-2 py-1">
          {mode === "device" ? "SRC: DEVICE CAM" : "SRC: ROBOT STREAM"}
        </div>

        {/* URL error state */}
        {mode === "url" && streamUrl && urlError && (
          <div className="absolute inset-0 grid place-items-center bg-[#0A0B0E]/80 p-4 text-center">
            <div>
              <VideoCameraSlash size={30} weight="bold" className="text-[#FF3B30] mx-auto mb-2" />
              <p className="text-xs text-[#FF3B30] uppercase tracking-widest">stream unreachable</p>
              <p className="text-[11px] text-[#475569] max-w-xs mx-auto mt-1">
                Make sure this device is on the same network as the robot and the URL is correct.
              </p>
              <button onClick={loadRobotStream} className="mt-3 inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-[#FF5A00] border border-[#272A35] px-2 py-1 hover:border-[#FF5A00]">
                <ArrowClockwise size={12} weight="bold" /> Retry
              </button>
            </div>
          </div>
        )}

        {/* standby state */}
        {!showingMedia && (
          <div className="absolute inset-0 grid place-items-center p-4 text-center">
            <div>
              <p className="text-[#475569] text-xs uppercase tracking-[0.3em]">feed standby</p>
              {mode === "url" ? (
                <p className="text-[11px] text-[#475569] max-w-xs mx-auto mt-2">
                  Enter your robot's camera stream URL above (e.g. an ESP32-CAM MJPEG link) and hit Load.
                </p>
              ) : (
                <p className="text-[11px] text-[#475569] max-w-xs mx-auto mt-2">
                  “Go Live” streams this device's camera. Switch to “Robot URL” to view your robot's own camera.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
